/**
 * Tiny GA4 helper. The gtag snippet itself is loaded in index.html (with
 * `send_page_view: false` so we own pageview firing for SPA routing).
 * This module exists so callers can import a typed `track()` and
 * `pageview()` instead of touching `window.gtag` directly — it also
 * silently no-ops if the snippet failed to load (ad blockers, dev with
 * the env unset, etc.) so the app keeps working.
 *
 * The shape we send mirrors GA4 recommended event names where possible
 * (https://developers.google.com/analytics/devguides/collection/ga4/reference/events)
 * — `page_view` is special-cased; everything else is a custom event name.
 */

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

function safeGtag(...args: unknown[]) {
  try {
    if (typeof window !== 'undefined' && typeof window.gtag === 'function') {
      window.gtag(...args);
    }
  } catch {
    // Analytics never blocks the app. Swallow.
  }
}

/**
 * Fire a SPA pageview. Pass the path you've just navigated to.
 * `page_title` defaults to whatever document.title is right now (which
 * the per-route title watcher in App.vue has already updated).
 */
export function pageview(path: string, title?: string) {
  safeGtag('event', 'page_view', {
    page_path: path,
    page_location: window.location.href,
    page_title: title ?? document.title,
  });
}

/**
 * Fire a custom event. Keep names in snake_case to match GA4 conventions
 * and so the events list in the GA UI stays sortable.
 */
export function track(eventName: string, params: Record<string, unknown> = {}) {
  safeGtag('event', eventName, params);
}
